<script setup>
import { ref, watch, nextTick } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import FormButton from '../Atoms/FormButton.vue'
import FormInput from '../Atoms/FormInput.vue'
import Alert from '../Atoms/Alert.vue'
import ModalBox from './ModalBox.vue'
import MediaflowService from '@/services/MediaflowService'
import { flowErrorMessage } from '@/components/Mediaflow/mediaflowMeta'

const props = defineProps({
	mediaflowId: {
		type: String,
		default: '',
		required: true,
	},
	flow: {
		type: Object,
		default: null,
	},
})

const { notify } = useNotification()

const isVisible = ref(false)
const error = ref(null)
const processing = ref(false)
const flowName = ref('')
const nameInput = ref(null)

const emit = defineEmits(['flow-updated'])

watch(() => props.flow, (newFlow) => {
	if (newFlow) flowName.value = newFlow.name || ''
}, { immediate: true })

const onReset = () => {
	flowName.value = ''
	error.value = null
	processing.value = false
}

// Kept for API compatibility with the parent (`editFlowModalRef.toggleSidebar()`).
const toggleSidebar = async () => {
	isVisible.value = !isVisible.value
	if (isVisible.value) {
		flowName.value = props.flow?.name || ''
		error.value = null
		await nextTick()
		nameInput.value?.focus?.()
	}
}

const updateFlow = async () => {
	const name = flowName.value.trim()
	if (!props.flow || !name || processing.value) return
	processing.value = true
	error.value = null
	try {
		const updatedFlow = await MediaflowService.updateFlow(props.mediaflowId, props.flow._id, name)
		emit('flow-updated', updatedFlow)
		notify({ group: 'success', text: 'Renamed' })
		isVisible.value = false
	} catch (err) {
		error.value = flowErrorMessage(err, 'Could not rename')
		notify({ group: 'error', text: error.value })
	} finally {
		processing.value = false
	}
}

defineExpose({ toggleSidebar })
</script>

<template>
	<ModalBox v-model="isVisible" :title="flow?.kind === 'break' ? 'Rename break' : 'Rename scene'" size="sm" placement="center" @close="onReset">
		<form class="flex flex-col gap-4 px-6 pb-2" @submit.prevent="updateFlow">
			<Alert v-if="error" type="error" class="!mb-0">{{ error }}</Alert>
			<FormInput
				id="edit-flow-name"
				ref="nameInput"
				v-model="flowName"
				label="Name"
				placeholder="Name"
				:maxlength="120"
				@keydown.enter.prevent="updateFlow"
			/>
		</form>
		<template #footer>
			<div class="flex flex-col-reverse gap-2 p-4 sm:flex-row sm:justify-end sm:p-6">
				<FormButton type="secondary" size="md" class="w-full justify-center sm:w-auto" :disabled="processing" @click="isVisible = false">Cancel</FormButton>
				<FormButton type="main" size="md" class="w-full justify-center sm:w-auto" :disabled="!flowName.trim() || flowName.trim() === (flow?.name || '')" :is-loading="processing" @click="updateFlow">Save</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
